import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect } from "react";
import { AiOutlineArrowLeft, AiOutlineClose } from "react-icons/ai";
import { twMerge } from "tailwind-merge";

interface PropTypes {
  isVisible: boolean;
  onHide: () => void;
  heading?: string;
  children: React.ReactNode;
  containerClassName?: string;
  modalClassName?: string;
  backButtonFunction?: () => void;
  showBackButton?: boolean;
}

const Modal = ({
  isVisible,
  onHide,
  children,
  heading,
  containerClassName,
  modalClassName,
  backButtonFunction,
  showBackButton
}: PropTypes) => {
  const contentRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
      // Scroll modal content to top when opened or children change
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isVisible, children]);

  return (
    <Transition.Root show={isVisible} as={Fragment}>
      <Dialog
        as="div"
        className={twMerge("fixed inset-0 z-50", modalClassName)}
        onClose={onHide}
      >
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        {/* Modal wrapper */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className='md:p-4 rounded-2xl lg:max-w-4xl lg:w-full w-full'>
              <div
                className={twMerge(
                  `
                w-full
                max-w-full
   md:max-w-5xl
     lg:max-w-6xl     
     xl:max-w-7xl
                max-h-[90vh]
                rounded-xl
                shadow-xl
                flex
                flex-col
                overflow-hidden
                border
                border-primary
                `,
                  containerClassName
                )}
              >

                <div className='flex items-center justify-between py-4 bg-primary px-2'>
                  <div className='w-8'>
                    {showBackButton && (
                      <button
                        onClick={backButtonFunction}
                        className='flex items-center justify-center'
                      >
                        <AiOutlineArrowLeft className="text-white text-xl" />
                      </button>
                    )}
                  </div>
                  {heading && (
                    <h1 className="text-xl font-bold text-white flex-1 text-center">{heading}</h1>
                  )}
                  <div className='w-8 flex items-center justify-end'>
                    <AiOutlineClose
                      className="cursor-pointer text-xl text-white"
                      onClick={onHide}
                    />
                  </div>
                </div>

                {/* Scrollable content */}
                <div ref={contentRef} className="flex-1 overflow-y-auto py-3 bg-white">
                  {children}
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Modal;